import { useTranslation } from 'react-i18next'

/**
 * Keys for the languages the app's own UI is offered in: each language named in its own
 * language, so a picker reads identically whichever session renders it. They live in `general`,
 * not the `Languages` namespace, which suffixes every entry with a country code ("Deutsch (DE)")
 * for the course-language lists.
 *
 * English is keyed `en-US` by legacy naming; the platform's English locale is en-GB, so for
 * English alone the tag and the key differ.
 */
const LANGUAGE_KEYS: Record<string, string> = {
  'de-DE': 'Languages.de-DE',
  'en-GB': 'Languages.en-US',
}

/**
 * The UI languages a picker offers, in display order. Matches `display_in_ui` on the languages
 * endpoint and the tags `UserMiddleware` is willing to apply, which are the real gate on what
 * the app can switch to.
 */
export const UI_LANGUAGE_TAGS = ['en-GB', 'de-DE']

/**
 * The UI language a detected locale belongs to. The language detector reports whatever the
 * browser carries - `en`, `en-US`, `de-AT` - and only an exact tag can be shown as selected, so
 * a near miss is resolved on the language part before falling back to the first offered tag.
 */
export const toUiLanguageTag = (language: string | undefined): string => {
  if (!language) return UI_LANGUAGE_TAGS[0]
  const languagePart = language.split('-')[0]

  return (
    UI_LANGUAGE_TAGS.find((tag) => tag === language) ??
    UI_LANGUAGE_TAGS.find((tag) => tag.split('-')[0] === languagePart) ??
    UI_LANGUAGE_TAGS[0]
  )
}

/**
 * Names a UI language for a language picker.
 */
export const useUiLanguageLabel = (): ((tag: string, displayName?: string) => string) => {
  const { t } = useTranslation(['general', 'Languages'])

  return (tag, displayName) =>
    LANGUAGE_KEYS[tag] ? t(LANGUAGE_KEYS[tag], { ns: 'general' }) : t(displayName ?? tag, { ns: 'Languages' })
}
